import { useMemo, useState } from "react";
import TurnoCardMedico from "../../../components/cards/TurnoCardMedico";
import Modal from "../../../components/modals/Modal";
import Toast from "../../../components/toast/Toast";
import HistorialPacienteModal from "../../../components/cards/HistorialPacienteModal";
import { IconCalendar, IconClock } from "../../../components/icons/IconsMedico";
import { EmptyState, FiltroEstado } from "../../../components/utils/Utils";
import { esProximo } from "../../../components/utils/MedicoUtils";
import "../../../components/toast/Toast.css";

const FILTROS_ESTADO = [
    { value: "todos", label: "Todos" },
    { value: "pendiente_confirmacion", label: "Pendiente de confirmación" },
    { value: "reservado", label: "Reservado" },
    { value: "confirmado", label: "Confirmado" },
    { value: "realizado", label: "Realizado" },
    { value: "cancelado", label: "Cancelado" },
];

const puedeCancelar = (turno) => {
    const fechaTurno = new Date(turno.fechaHora);
    const unaHoraAntes = new Date(fechaTurno.getTime() - 60 * 60 * 1000);
    return new Date() <= unaHoraAntes;
};

const toDatetimeLocal = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatFechaLarga = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "fecha no disponible";
    return date.toLocaleString("es-AR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const MisTurnos = ({ turnos = [], loading, error, onActualizarTurno, onProponerCambio, onAceptarCambio, onRechazarCambio }) => {
    const [editando, setEditando] = useState(null);
    const [nuevaFecha, setNuevaFecha] = useState("");
    const [viendoPaciente, setViendoPaciente] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [toastError, setToastError] = useState("");
    const [filtroEstado, setFiltroEstado] = useState("todos");

    const turnosFiltrados = useMemo(() => {
        if (filtroEstado === "todos") return turnos;
        return turnos.filter((t) => t.estado === filtroEstado);
    }, [turnos, filtroEstado]);

    const { proximos, historial } = useMemo(() => {
        return turnosFiltrados.reduce(
            (groups, turno) => {
                if (esProximo(turno)) {
                    groups.proximos.push(turno);
                } else {
                    groups.historial.push(turno);
                }
                return groups;
            },
            { proximos: [], historial: [] },
        );
    }, [turnosFiltrados]);

    const abrirEdicion = (turno) => {
        setToastError("");
        setEditando(turno);
        setNuevaFecha(toDatetimeLocal(turno.fechaHora));
    };

    const guardarCambioFecha = async () => {
        if (!nuevaFecha) {
            setToastError("Seleccioná una fecha y hora.");
            return;
        }
        setGuardando(true);
        setToastError("");
        try {
            await onProponerCambio(editando, new Date(nuevaFecha).toISOString());
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleAceptar = async (turno) => {
        setGuardando(true);
        setToastError("");
        try {
            await onAceptarCambio(turno);
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleRechazar = async (turno) => {
        setGuardando(true);
        setToastError("");
        try {
            await onRechazarCambio(turno);
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const cambiarEstado = async (turno, nuevoEstado) => {
        if (nuevoEstado === "cancelado" && !puedeCancelar(turno)) {
            setToastError("No es posible cancelar el turno con menos de 1 hora de anticipación.");
            return;
        }
        try {
            await onActualizarTurno(turno.id, { estado: nuevoEstado.toUpperCase() });
        } catch (e) {
            setToastError(e.message);
        }
    };

    const esPendiente = editando?.estado === "pendiente_confirmacion";

    return (
        <div className="medico-section">
            <div className="medico-section__header">
                <div>
                    <p className="eyebrow">Agenda</p>
                    <h2>Mis turnos</h2>
                </div>
            </div>

            <FiltroEstado options={FILTROS_ESTADO} value={filtroEstado} onChange={setFiltroEstado} />

            {error && <div className="patient-state patient-state--error">{error}</div>}

            {loading ? (
                <div className="patient-state">
                    <div className="patient-state__loader" />
                    <p>Cargando turnos...</p>
                </div>
            ) : (
                <>
                    <div className="turnos-group">
                        <h3 className="turnos-group__title">Próximos · {proximos.length}</h3>
                        {proximos.length === 0 ? (
                            <EmptyState icon={<IconCalendar />} title="No tenés turnos próximos." />
                        ) : (
                            <div className="turnos-list">
                                {proximos.map((t) => (
                                    <TurnoCardMedico
                                        key={t.id}
                                        turno={t}
                                        acciones
                                        onVerPaciente={setViendoPaciente}
                                        onConfirmar={(turno) => cambiarEstado(turno, "confirmado")}
                                        onMarcarRealizado={(turno) => cambiarEstado(turno, "realizado")}
                                        onCancelar={(turno) => cambiarEstado(turno, "cancelado")}
                                        onEditar={abrirEdicion}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="turnos-group">
                        <h3 className="turnos-group__title">Historial · {historial.length}</h3>
                        {historial.length === 0 ? (
                            <EmptyState icon={<IconClock />} title="Sin historial de turnos." />
                        ) : (
                            <div className="turnos-list">
                                {historial.map((t) => (
                                    <TurnoCardMedico
                                        key={t.id}
                                        turno={t}
                                        acciones
                                        onVerPaciente={setViendoPaciente}
                                        onEditar={abrirEdicion}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {editando && (
                <Modal
                    title={esPendiente ? "Solicitud de cambio de fecha" : "Proponer cambio de fecha"}
                    onClose={() => setEditando(null)}
                >
                    <div className="modal-form">
                        {esPendiente ? (
                            editando.propuestoPor === "MEDICO" ? (
                                <p className="modal-info">
                                    Propusiste cambiar este turno al{" "}
                                    <strong>{formatFechaLarga(editando.fechaHoraPropuesta)}</strong>.
                                    Esperando que el paciente acepte o rechace.
                                </p>
                            ) : (
                                <>
                                    <p className="modal-info">
                                        El paciente propuso cambiar este turno al{" "}
                                        <strong>{formatFechaLarga(editando.fechaHoraPropuesta)}</strong>. ¿Aceptás el nuevo horario?
                                    </p>
                                    <div className="modal-form__actions">
                                        <button className="btn-ghost btn-ghost--danger" onClick={() => handleRechazar(editando)} disabled={guardando}>
                                            Rechazar
                                        </button>
                                        <button className="primary-button" onClick={() => handleAceptar(editando)} disabled={guardando}>
                                            {guardando ? "Guardando..." : "Aceptar cambio"}
                                        </button>
                                    </div>
                                </>
                            )
                        ) : (
                            <>
                                <label className="form-field">
                                    <span>Nueva fecha y hora</span>
                                    <input
                                        type="datetime-local"
                                        value={nuevaFecha}
                                        onChange={(e) => setNuevaFecha(e.target.value)}
                                    />
                                </label>
                                <p className="modal-hint">
                                    El turno pasará a <strong>Pendiente de confirmación</strong> hasta que la otra parte acepte.
                                </p>
                                <div className="modal-form__actions">
                                    <button className="btn-ghost" onClick={() => setEditando(null)}>
                                        Cancelar
                                    </button>
                                    <button className="primary-button" onClick={guardarCambioFecha} disabled={guardando}>
                                        {guardando ? "Enviando..." : "Proponer cambio"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {viendoPaciente && (
                <HistorialPacienteModal
                    paciente={viendoPaciente}
                    onClose={() => setViendoPaciente(null)}
                />
            )}

            <Toast message={toastError} onClose={() => setToastError("")} />
        </div>
    );
};

export default MisTurnos;